// src/encounter/services/use-l360-activation.ts
// L360 A1-A3 data layer (TanStack Query). The panel imports nothing else that talks to the network.
//   A1 useSignGoal      sign one proposed target; a 412 refreshes the plan (the caller shows the message).
//   A2 useCareTeam / useCreateTeamTasks / useClaimTask  (Accept posts careteam.assignment_recorded after the claim saves; dev flag)
//   M4 useL360TargetsSignedEvent  posts targets.review_signed once every M4 target is signed and the run waits at M4 (dev flag)
//   A3 useA3Enrolment / useCareModelRun / useApproveA3 / useRaiseDemoHold / useReleaseHold  (Workflows host)
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/auth/auth-store';
import { env } from '@/config/env';
import { DEMO_HOLD_BODY, holdStepsToRelease, newApproveEventKey, pickEnrolment, TEAM_ROLES, targetsSignedEvent, teamTaskBody } from '../lib/l360-activation';
import type { PatientEnrolmentWire } from '@/types/care-model-api';
import type { CareModelRunWire, ClinicalTaskWire, CareTeamResponse, CompleteA3Request, CompleteA3Result, PatientActivationSnapshot, PatientRevisions, RunHoldWire, RunRevisions } from '@/types/l360-api';
import type { CarePlanView } from '../lib/care-plan-view';
import { carePlansKey, patientEnrolmentsKey } from './use-care-plans';
import { fetchPatientEnrolments } from './care-model-api';
import {
  claimTask,
  createTask,
  duplicateTaskId,
  fetchActivationSnapshot,
  fetchCareModelRun,
  fetchCareTeam,
  postA3Complete,
  postHold,
  postL360Event,
  signGoal,
  transitionHold,
} from './l360-api';

export function careTeamKey(patientId: string | null | undefined, carePlanId: string) {
  return ['l360-care-team', patientId, carePlanId] as const;
}

/** A2 · the team list. */
export function useCareTeam(patientId: string | null | undefined, carePlanId: string): UseQueryResult<CareTeamResponse> {
  return useQuery<CareTeamResponse>({
    queryKey: careTeamKey(patientId, carePlanId),
    enabled: !!patientId,
    queryFn: ({ signal }) => fetchCareTeam(patientId as string, carePlanId, signal),
  });
}

/** A1 · sign one target against the revision the panel last read. Settled either way: the plan is read again. */
export function useSignGoal(
  patientId: string | null | undefined,
  carePlanId: string,
): UseMutationResult<unknown, Error, { goalId: string; expectedRevision: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, expectedRevision }) => signGoal(patientId as string, carePlanId, goalId, { expectedRevision }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: carePlansKey(patientId) });
      void qc.invalidateQueries({ queryKey: careTeamKey(patientId, carePlanId) });
    },
  });
}

export interface TeamTaskOutcome {
  role: string;
  result: 'created' | 'existing' | 'failed';
}

/** A2 · one request per role. Every role is tried; a 409 `duplicate-open-task` means
 * "already exists", any other error marks that role `failed`. One result per role, always. */
export function useCreateTeamTasks(
  patientId: string | null | undefined,
  carePlanId: string,
): UseMutationResult<TeamTaskOutcome[], Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const outcomes: TeamTaskOutcome[] = [];
      for (const role of TEAM_ROLES) {
        try {
          await createTask(patientId as string, teamTaskBody(role));
          outcomes.push({ role, result: 'created' });
        } catch (e) {
          outcomes.push({ role, result: duplicateTaskId(e) === null ? 'failed' : 'existing' });
        }
      }
      return outcomes;
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: careTeamKey(patientId, carePlanId) }),
  });
}

/**
 * A2 · Accept = the existing Claim, with the version it was read at. After the claim saves (never on error, never
 * awaited) it tells the L360 run `careteam.assignment_recorded`; the claimant is the current user. The mutation result
 * is the claim result, unchanged.
 */
export function useClaimTask(
  patientId: string | null | undefined,
  carePlanId: string,
): UseMutationResult<ClinicalTaskWire, Error, { taskId: string; version: number }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, version }) => claimTask(patientId as string, taskId, { version }),
    // The event is a side effect of the claim: nothing in here may fail the mutation (a throw would show an Accept error).
    onSuccess: (data, { taskId }) => {
      try {
        if (!patientId) return;
        const assigneeId = useAuthStore.getState().user?.userId;
        if (!assigneeId) {
          console.warn('L360 event careteam.assignment_recorded not posted: no signed-in user id');
          return;
        }
        if (typeof data?.version !== 'number') {
          console.warn('L360 event careteam.assignment_recorded not posted: the claim returned no task version');
          return;
        }
        postL360Event({
          eventType: 'careteam.assignment_recorded',
          patientId,
          sourceRef: taskId,
          revision: data.version,
          payload: { carePlanId, assignments: [{ taskKey: taskId, assigneeId }] },
        }).catch(() => {});
      } catch (err) {
        console.warn('L360 event careteam.assignment_recorded not posted', err);
      }
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: careTeamKey(patientId, carePlanId) }),
  });
}

/** Prefix that matches every care-model run query (any enrolment). */
export const careModelRunPrefix = ['care-model-run'] as const;

export function careModelRunKey(enrolmentId: string | null | undefined) {
  return [...careModelRunPrefix, enrolmentId] as const;
}

/** A3 · the patient's enrolment for the plan's definition (shares the C4 cache entry with the care-plan readout). */
export function useA3Enrolment(patientId: string | null | undefined, definitionId: string | null): UseQueryResult<PatientEnrolmentWire | null> {
  return useQuery<PatientEnrolmentWire[], Error, PatientEnrolmentWire | null>({
    queryKey: patientEnrolmentsKey(patientId),
    enabled: !!patientId,
    queryFn: ({ signal }) => (patientId ? fetchPatientEnrolments(patientId, signal) : Promise.resolve([])),
    select: (all) => pickEnrolment(all, definitionId),
  });
}

/** A3 · the run, read from Workflows. The panel shows this and nothing else as the decision. */
export function useCareModelRun(enrolmentId: string | null | undefined): UseQueryResult<CareModelRunWire> {
  return useQuery<CareModelRunWire>({
    queryKey: careModelRunKey(enrolmentId),
    enabled: !!enrolmentId,
    queryFn: ({ signal }) => fetchCareModelRun(enrolmentId as string, signal),
  });
}

export function activationSnapshotKey(patientId: string | null | undefined, carePlanId: string | null | undefined) {
  return ['l360-activation-snapshot', patientId, carePlanId] as const;
}

/** A3 · the Patient plan and consent revisions. `null` data = the Patient endpoint is not deployed (404 or 503). */
export function useActivationSnapshot(
  patientId: string | null | undefined,
  carePlanId: string | null | undefined,
): UseQueryResult<PatientActivationSnapshot | null> {
  return useQuery<PatientActivationSnapshot | null>({
    queryKey: activationSnapshotKey(patientId, carePlanId),
    enabled: !!patientId && !!carePlanId,
    queryFn: ({ signal }) => fetchActivationSnapshot(patientId as string, carePlanId as string, signal),
  });
}

export interface ApproveA3Vars {
  revisions: RunRevisions;
  /** null leaves `expectedPatientRevisions` out of the body. */
  patient: PatientRevisions | null;
}

/**
 * A3 · Approve against the STORED snapshot, with a new eventKey per click. 409 held and stale come back as results and
 * are never retried. Only a routed result refreshes the run; the snapshot is state in the panel, never replaced here.
 */
export function useApproveA3(enrolmentId: string): UseMutationResult<CompleteA3Result, Error, ApproveA3Vars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ revisions, patient }) => {
      const body: CompleteA3Request = {
        eventKey: newApproveEventKey(),
        outcomes: ['approved'],
        expectedRevisions: revisions,
        ...(patient ? { expectedPatientRevisions: { planRevision: patient.planRevision, consentRevision: patient.consentRevision } } : {}),
      };
      return postA3Complete(enrolmentId, body);
    },
    onSuccess: (result) => {
      if (result.kind === 'routed') void qc.invalidateQueries({ queryKey: careModelRunKey(enrolmentId) });
    },
  });
}

/** A3 · the demo hold: a real hold on A3 in the dev run. The run is read again; the snapshot is not. */
export function useRaiseDemoHold(enrolmentId: string): UseMutationResult<unknown, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postHold(enrolmentId, { eventKey: `a3:demo-hold:${crypto.randomUUID()}`, scopeActivityIds: ['A3'], ...DEMO_HOLD_BODY }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: careModelRunKey(enrolmentId) }),
  });
}

export interface ReleaseHoldVars {
  hold: RunHoldWire;
  /** Needed only when the hold has not yet reached `disposition_recorded`. */
  disposition?: string;
  /** The signed-in user id, kept in the release reference. */
  signedBy: string;
}

export type ReleaseHoldResult = { kind: 'released' } | { kind: 'stale' } | { kind: 'not-releasable' };

/** The run (before the walk) or a step response (during it) has no usable revision for the hold. Missing data, not stale. */
export class HoldRevisionMissingError extends Error {
  constructor() {
    super('hold revision missing');
    this.name = 'HoldRevisionMissingError';
  }
}

/**
 * A whole number of 0 or more, or null when the revision is absent, blank, negative, fractional or not plain digits
 * (Number(null) and Number('') are 0; Number('0x10') is 16).
 */
function readHoldRevision(value: RunHoldWire['holdRevision']): number | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return null;
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/**
 * A3 · Release hold: walks the remaining steps one POST at a time with ONE click id, each step on the revision the
 * previous response returned. The disposition goes only on `disposition_recorded` (a hold already past it keeps the one
 * the server has); the signed reference goes only on `released`. A state with no steps returns `not-releasable` and sends
 * nothing. A missing revision (on the hold before the walk, or in a response when another step follows) throws
 * HoldRevisionMissingError and sends nothing more. A stale result stops the walk.
 */
export function useReleaseHold(enrolmentId: string): UseMutationResult<ReleaseHoldResult, Error, ReleaseHoldVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ hold, disposition, signedBy }) => {
      const clickId = crypto.randomUUID();
      const steps = holdStepsToRelease(hold.acknowledgmentState);
      if (steps.length === 0) return { kind: 'not-releasable' };
      let revision = readHoldRevision(hold.holdRevision);
      if (revision === null) throw new HoldRevisionMissingError();
      for (const [i, to] of steps.entries()) {
        const note = (disposition ?? '').trim();
        if (to === 'disposition_recorded' && note === '') throw new Error('disposition missing');
        const result = await transitionHold(enrolmentId, hold.holdId, {
          eventKey: `ap-panel:A3:hold:${hold.holdId}:${to}:${crypto.randomUUID()}`,
          to,
          expectedHoldRevision: revision,
          ...(to === 'disposition_recorded' ? { disposition: note } : {}),
          ...(to === 'released' ? { releaseAuthorizationRef: `ap-panel:signed-disposition:${signedBy}:${clickId}` } : {}),
        });
        if (result.kind === 'stale') return { kind: 'stale' };
        if (i === steps.length - 1) break; // released: no further step needs a revision
        revision = readHoldRevision(result.run.holds?.find((h) => h.holdId === hold.holdId)?.holdRevision);
        if (revision === null) throw new HoldRevisionMissingError();
      }
      return { kind: 'released' };
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: careModelRunKey(enrolmentId) }),
  });
}

/** M4 · idempotency keys already posted or in flight in this page session. Exported so a test can clear it. */
export const targetsSignedPosted = new Map<string, 'pending' | 'done'>();

/**
 * M4 · tells Workflows `targets.review_signed` once every M4 target of the plan is signed and the run waits at M4.
 * One effect over the plan and run data covers both a sign (the panel then reads both again) and a panel load that finds
 * the plan already signed. FIRE-AND-FORGET: no toast, no UI change, nothing thrown into render. Off unless
 * env.l360EventsEnabled (dev only). A `failed` or rejected post frees the key, so the next time the plan or run is read
 * again (a sign, a reload, a window refocus) it is sent again; there is no timed retry. Accepted refreshes the run.
 */
export function useL360TargetsSignedEvent(patientId: string | null | undefined, plan: CarePlanView): void {
  const qc = useQueryClient();
  const enrolmentId = useA3Enrolment(patientId, plan.definitionId).data?.enrolmentId ?? null;
  const run = useCareModelRun(enrolmentId).data;
  useEffect(() => {
    try {
      if (!env.l360EventsEnabled || !patientId) return;
      const event = targetsSignedEvent(plan, run);
      if (!event) return;
      const { carePlanId } = plan;
      const key = `targets.review_signed:${carePlanId}:${event.planRevision}`;
      if (targetsSignedPosted.has(key)) return;
      // Set BEFORE the request, so a re-render or StrictMode's second effect cannot post the same key.
      targetsSignedPosted.set(key, 'pending');
      // Called inside .then, so a synchronous throw from the call also lands in .catch and frees the key.
      void Promise.resolve()
        .then(() =>
          postL360Event({
            eventType: 'targets.review_signed',
            patientId,
            sourceRef: carePlanId,
            revision: event.planRevision,
            payload: { carePlanId, goalIds: event.goalIds, planRevision: event.planRevision },
          }),
        )
        .then((result) => {
          if (result?.kind === 'failed') {
            targetsSignedPosted.delete(key);
            return;
          }
          targetsSignedPosted.set(key, 'done');
          if (result?.kind === 'accepted' && enrolmentId) void qc.invalidateQueries({ queryKey: careModelRunKey(enrolmentId) });
        })
        .catch(() => targetsSignedPosted.delete(key));
    } catch (err) {
      console.warn('L360 event targets.review_signed not posted', err);
    }
  }, [patientId, plan, run, enrolmentId, qc]);
}
